/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        One-Time Secret Key Reveal Modal (Task 10.6.1)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState } from 'react';
import {
  ShieldAlert,
  Copy,
  Check,
  Code2,
  CheckCircle2,
} from 'lucide-react';
import { useTranslation } from '../../i18n';
import {
  useApiKeyStore,
  generateCurlSnippet,
  generateNodeSnippet,
  generatePythonSnippet,
} from '../../stores/useApiKeyStore';

export const SecretKeyRevealModal: React.FC = () => {
  const { t } = useTranslation();
  const {
    isRevealModalOpen,
    newlyCreatedKey,
    clearNewlyCreatedKey,
  } = useApiKeyStore();

  const [copiedKey, setCopiedKey] = useState(false);
  const [activeSnippetTab, setActiveSnippetTab] = useState<'curl' | 'node' | 'python'>('curl');
  const [copiedSnippet, setCopiedSnippet] = useState(false);

  if (!isRevealModalOpen || !newlyCreatedKey) return null;

  const { apiKey, secretKey } = newlyCreatedKey;

  const handleCopyKey = async () => {
    try {
      await navigator.clipboard.writeText(secretKey);
      setCopiedKey(true);
      setTimeout(() => setCopiedKey(false), 2000);
    } catch {
      // Fallback
    }
  };

  const currentSnippet =
    activeSnippetTab === 'curl'
      ? generateCurlSnippet(secretKey)
      : activeSnippetTab === 'node'
      ? generateNodeSnippet(secretKey)
      : generatePythonSnippet(secretKey);

  const handleCopySnippet = async () => {
    try {
      await navigator.clipboard.writeText(currentSnippet);
      setCopiedSnippet(true);
      setTimeout(() => setCopiedSnippet(false), 2000);
    } catch {
      // Fallback
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-xs animate-in fade-in duration-150">
      <div className="bg-surface rounded-2xl border border-border shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[92vh] animate-in zoom-in-95 duration-150">
        {/* Header */}
        <div className="p-5 border-b border-border bg-surface flex items-center gap-3">
          <span className="p-2.5 rounded-xl bg-amber-500/10 text-amber-500 border border-amber-500/20">
            <ShieldAlert className="size-5" />
          </span>
          <div>
            <h3 className="text-base font-bold text-text">
              {t('apikeys.revealModalTitle')}
            </h3>
            <p className="text-xs text-text-muted">
              {apiKey.name} ({apiKey.keyPrefix}••••••••{apiKey.keyHint})
            </p>
          </div>
        </div>

        {/* Modal Content */}
        <div className="p-6 overflow-y-auto space-y-5 text-xs flex-1">
          {/* 1. Critical Security Warning Alert */}
          <div className="rounded-xl border border-rose-500/30 bg-rose-500/5 p-4 text-rose-600 dark:text-rose-400 leading-relaxed flex items-start gap-3">
            <ShieldAlert className="size-5 shrink-0 mt-0.5" />
            <div>
              <div className="font-bold text-xs mb-1 uppercase tracking-wider">
                Security Notice
              </div>
              <p className="text-xs opacity-90">
                {t('apikeys.revealModalWarning')}
              </p>
            </div>
          </div>

          {/* 2. Plaintext Secret Key Box */}
          <div className="space-y-1.5">
            <label className="font-semibold text-text block">
              {t('apikeys.colKey')}
            </label>
            <div className="flex items-center gap-2 rounded-xl border border-border bg-surface-subtle p-2">
              <input
                type="text"
                readOnly
                value={secretKey}
                className="flex-1 bg-transparent font-mono text-xs text-text px-2 select-all focus:outline-none"
              />
              <button
                type="button"
                onClick={handleCopyKey}
                className="px-3.5 py-1.5 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 text-xs font-semibold flex items-center gap-1.5 transition-colors shadow-xs"
              >
                {copiedKey ? (
                  <>
                    <Check className="size-3.5" />
                    <span>Copied!</span>
                  </>
                ) : (
                  <>
                    <Copy className="size-3.5" />
                    <span>{t('apikeys.copyKeyBtn')}</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* 3. Quickstart Code Snippets */}
          <div className="space-y-2 pt-2 border-t border-border">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Code2 className="size-4 text-primary" />
                <span className="font-semibold text-text">
                  {t('apikeys.quickstartTitle')}
                </span>
              </div>

              {/* Snippet Language Switcher */}
              <div className="p-0.5 rounded-lg bg-surface-subtle border border-border flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setActiveSnippetTab('curl')}
                  className={`px-2.5 py-1 rounded text-[11px] font-mono transition-all ${
                    activeSnippetTab === 'curl'
                      ? 'bg-surface text-text font-bold shadow-xs'
                      : 'text-text-muted hover:text-text'
                  }`}
                >
                  cURL
                </button>
                <button
                  type="button"
                  onClick={() => setActiveSnippetTab('node')}
                  className={`px-2.5 py-1 rounded text-[11px] font-mono transition-all ${
                    activeSnippetTab === 'node'
                      ? 'bg-surface text-text font-bold shadow-xs'
                      : 'text-text-muted hover:text-text'
                  }`}
                >
                  Node.js
                </button>
                <button
                  type="button"
                  onClick={() => setActiveSnippetTab('python')}
                  className={`px-2.5 py-1 rounded text-[11px] font-mono transition-all ${
                    activeSnippetTab === 'python'
                      ? 'bg-surface text-text font-bold shadow-xs'
                      : 'text-text-muted hover:text-text'
                  }`}
                >
                  Python
                </button>
              </div>
            </div>

            {/* Code Box */}
            <div className="relative rounded-xl border border-border bg-surface-subtle p-3.5 font-mono text-[11px] text-text whitespace-pre-wrap leading-relaxed select-all max-h-48 overflow-y-auto">
              <button
                type="button"
                onClick={handleCopySnippet}
                className="absolute top-2.5 right-2.5 p-1.5 rounded-lg border border-border bg-surface hover:bg-surface-subtle text-text-muted hover:text-text transition-colors shadow-xs"
                title="Copy code snippet"
              >
                {copiedSnippet ? (
                  <Check className="size-3.5 text-emerald-500" />
                ) : (
                  <Copy className="size-3.5" />
                )}
              </button>
              {currentSnippet}
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="p-4 border-t border-border bg-surface-subtle flex items-center justify-end">
          <button
            type="button"
            onClick={clearNewlyCreatedKey}
            className="px-5 py-2.5 text-xs font-semibold rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 transition-all shadow-sm flex items-center gap-2"
          >
            <CheckCircle2 className="size-4" />
            <span>{t('apikeys.savedConfirmBtn')}</span>
          </button>
        </div>
      </div>
    </div>
  );
};
